import React, { useEffect, useState, useRef, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api, { formatApiError, getToken, popDraft } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { CLIENT_STATUSES, statusMeta, normalizeStatus, clientBadge } from "@/lib/clientStatus";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { NumberField } from "@/components/NumberField";
import { InfoTip } from "@/components/InfoTip";
import { TIPS } from "@/lib/infoTips";
import { ClientStatusBar } from "@/components/ClientStatusBar";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import {
  ArrowLeft, Save, Trash2, HeartHandshake, Sparkles, Scissors,
  FileText, ScrollText, MapPin, Plus, Send, Download, ExternalLink, Clock,
  Mail, MessageCircle, Smartphone, Loader2, Link as LinkIcon, Copy,
  Receipt, CheckCircle2, AlertCircle, Briefcase, Lock, Instagram, CalendarHeart,
  LayoutDashboard, Wallet, CalendarDays, Cloud, Image as ImageIcon, AlertTriangle,
} from "lucide-react";
import { toast } from "sonner";
import TimelineTab from "@/pages/TimelineTab";
import WeddingDayTab from "@/pages/WeddingDayTab";
import ClientOverview from "@/components/ClientOverview";
import WeddingPlannerBlock from "@/components/WeddingPlannerBlock";
import ClientAvatar from "@/components/ClientAvatar";
import SafeBoundary from "@/components/SafeBoundary";
import PlanLock from "@/components/PlanLock";
import TrialBlock from "@/pages/TrialBlock";
import MakeupSheet from "@/pages/MakeupSheet";
import HairSheet from "@/pages/HairSheet";
import MakeupTab from "@/pages/ClientDetail/tabs/MakeupTab";
import HairTab from "@/pages/ClientDetail/tabs/HairTab";
import MapTab from "@/pages/ClientDetail/tabs/MapTab";
import { TimePickerField } from "@/components/TimePicker";
import { BillingForm, validateBilling } from "@/components/BillingForm";
import { useLeads } from "@/context/LeadsContext";
import ShareAction from "@/components/ShareAction";
import ArtistPicker from "@/components/ArtistPicker";
import AssistantsPicker from "@/components/AssistantsPicker";
import { QuoteItemsMobile, QuoteTotalBanner } from "@/components/QuoteItemsMobile";
import { QuoteMobileActionBar } from "@/components/QuoteMobileActionBar";
import WeddingMobileAccordion from "@/components/client/WeddingMobileAccordion";
import ContactsQuickRow from "@/components/client/ContactsQuickRow";
import VendorsMobileView from "@/components/client/VendorsMobileView";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";

const QUOTE_STATUSES = [
  { v: "bozza", label: "Bozza" },
  { v: "inviato", label: "Inviato" },
  { v: "accettato", label: "Accettato" },
  { v: "rifiutato", label: "Rifiutato" },
];
const CONTRACT_STATUSES = [
  { v: "non_inviato", label: "Non inviato" },
  { v: "inviato", label: "Inviato" },
  { v: "firmato", label: "Firmato" },
];

// "Note rapide" predefinite per il preventivo — id stabili sincronizzati col backend
// (vedi QUICK_NOTE_TEMPLATES in /app/backend/constants.py). L'ordine qui determina anche
// l'ordine di rendering nel PDF e nel portale pubblico.
//
// La label è localizzata in base al campo `language` del cliente:
//   • client.language === "it"  → italiano
//   • qualunque altra lingua    → inglese (fallback)
// Il backend applica esattamente la stessa logica (constants.quick_note_templates_for).
const QUOTE_QUICK_NOTES_IT = [
  {
    id: "qn_contract_after_trial",
    label: "Il contratto potrà essere firmato successivamente alla prova acconciatura o trucco.",
  },
  {
    id: "qn_trial_payment",
    label:
      "La prova acconciatura o trucco verrà saldata il giorno stesso della prova. In caso di conferma e firma del contratto, l'importo verrà scalato dal totale finale.",
  },
  {
    id: "qn_date_confirmation",
    label: "La data sarà confermata solo al versamento dell'acconto concordato o alla firma del contratto.",
  },
];

const QUOTE_QUICK_NOTES_EN = [
  {
    id: "qn_contract_after_trial",
    label: "The contract may be signed after the hair or makeup trial.",
  },
  {
    id: "qn_trial_payment",
    label:
      "The hair or makeup trial is paid on the day of the trial itself. If the contract is later confirmed and signed, the amount will be deducted from the final balance.",
  },
  {
    id: "qn_date_confirmation",
    label: "The wedding date is confirmed only upon payment of the agreed deposit or upon signing the contract.",
  },
];

const quickNotesForLanguage = (lang) =>
  (lang || "").toString().trim().toLowerCase() === "it"
    ? QUOTE_QUICK_NOTES_IT
    : QUOTE_QUICK_NOTES_EN;

// Servizi predefiniti per la "Descrizione" delle voci del preventivo.
// Implementati come datalist HTML5: l'utente può sceglierne uno O scrivere
// manualmente un testo personalizzato. La voce sentinella "Altro …" reset il campo.
const QUOTE_SERVICE_CUSTOM_SENTINEL = "Altro (inserisci manualmente)";
const QUOTE_SERVICE_OPTIONS = [
  "Trucco sposa",
  "Acconciatura sposa",
  "Prova trucco sposa",
  "Prova acconciatura sposa",
  "Trucco sposa + acconciatura sposa",
  "Trucco ospiti",
  "Acconciatura ospiti",
  "Piega ospiti",
  "Trucco + acconciatura ospiti",
  "Cambio look durante evento",
  "Assistenza in loco sposa",
  "Assistenza in loco ospiti",
  QUOTE_SERVICE_CUSTOM_SENTINEL,
];

// Lista sintetica paesi — lingua auto-suggerita quando cambia la nazionalità.
// Il flag "suggest" usa i codici lingua riconosciuti dal backend (it/en/es/fr).
const NATIONALITIES = [
  { code: "IT", label: "Italia", suggest: "it" },
  { code: "GB", label: "Regno Unito", suggest: "en" },
  { code: "US", label: "Stati Uniti", suggest: "en" },
  { code: "IE", label: "Irlanda", suggest: "en" },
  { code: "AU", label: "Australia", suggest: "en" },
  { code: "CA", label: "Canada", suggest: "en" },
  { code: "ES", label: "Spagna", suggest: "es" },
  { code: "MX", label: "Messico", suggest: "es" },
  { code: "AR", label: "Argentina", suggest: "es" },
  { code: "FR", label: "Francia", suggest: "fr" },
  { code: "BE", label: "Belgio", suggest: "fr" },
  { code: "CH", label: "Svizzera", suggest: null },
  { code: "DE", label: "Germania", suggest: null },
  { code: "AT", label: "Austria", suggest: null },
  { code: "NL", label: "Paesi Bassi", suggest: null },
  { code: "PT", label: "Portogallo", suggest: null },
  { code: "BR", label: "Brasile", suggest: null },
  { code: "OTHER", label: "Altro", suggest: null },
];

const LANGUAGES = [
  { v: "it", label: "Italiano" },
  { v: "en", label: "Inglese" },
  { v: "es", label: "Spagnolo" },
  { v: "fr", label: "Francese" },
];

const fmtEuro = (n) =>
  new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR" }).format(parseFloat(n) || 0);

export default function ClientDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const { refresh: refreshLeads } = useLeads();
  const navigate = useNavigate();
  const [client, setClient] = useState(null);
  const [busy, setBusy] = useState(false);
  // ----- Autosave state -----
  // status: 'idle' | 'saving' | 'saved' | 'error'
  // lastSavedSnapshotRef holds JSON.stringify of the last server-confirmed client.
  // We diff it with the current `client` to determine "dirty" without keeping a 2nd copy in state.
  const [autosaveStatus, setAutosaveStatus] = useState("idle");
  const lastSavedSnapshotRef = useRef("");
  const autosaveTimerRef = useRef(null);
  const inFlightRef = useRef(false);
  // Used to skip first render (avoid an autosave triggered by initial load).
  const initialLoadRef = useRef(true);
  const [shareOpen, setShareOpen] = useState(false);
  // Send-options (replaces the legacy single "Invia preventivo + contratto" button):
  // the operator picks WHICH documents to mark as sent/visible to the client.
  // Default: send only the quote (so the client can review/optionally upsell
  // before the contract is presented for signature).
  const [sendQuote, setSendQuote] = useState(true);
  const [sendContract, setSendContract] = useState(false);
  // Cached PDF blob during the open dialog session (avoid re-fetch). Invalidated
  // whenever the operator changes which documents to include OR the dialog closes,
  // so each share action always uses a PDF that matches the current flags.
  const [mergedPdfBlob, setMergedPdfBlob] = useState(null);
  const [preparingPdf, setPreparingPdf] = useState(false);
  const [shareBusyAction, setShareBusyAction] = useState("");

  // Invalidate the cached PDF whenever the operator changes the include flags,
  // OR closes the dialog. This guarantees that the next share action uses a PDF
  // that matches the currently-selected documents (avoids the bug where toggling
  // 'Invia contratto' after opening the dialog would still share a quote-only PDF).
  useEffect(() => {
    setMergedPdfBlob(null);
  }, [sendQuote, sendContract]);
  useEffect(() => {
    if (!shareOpen) setMergedPdfBlob(null);
  }, [shareOpen]);
  // Public share token for the client (shared with timeline link, lazy-created)
  const [clientShareToken, setClientShareToken] = useState("");
  // Controlled active tab — lifted so the Panoramica overview can switch
  // to other tabs (Preventivo, Wedding Day, Timeline) when the user
  // clicks a card or a "Vai a…" CTA.
  const [activeTab, setActiveTab] = useState("overview");
  // Team members (for operator assignment in Makeup/Hair tabs)
  const [team, setTeam] = useState([]);
  useEffect(() => {
    api.get("/team").then((r) => setTeam(r.data || [])).catch(() => {});
  }, []);

  const mods = user?.modules || [];
  const hasMakeup = mods.includes("makeup");
  const hasHair = mods.includes("hair");

  useEffect(() => {
    api.get(`/clients/${id}`).then((r) => {
      const c = ensureDefaults(r.data);
      // Session-expired recovery: if the user was kicked to /login mid-edit
      // while the autosave PUT was in flight, the api interceptor persisted
      // the body under a known key. Re-merge it onto the freshly-loaded
      // server state so the operator continues from where they left off.
      let recovered = null;
      try { recovered = popDraft(`PUT /clients/${id}`); } catch {/* noop */}
      const merged = recovered?.payload && typeof recovered.payload === "object"
        ? ensureDefaults({ ...c, ...recovered.payload, id: c.id, owner_id: c.owner_id })
        : c;
      setClient(merged);
      // Mark as dirty so autosave kicks in immediately and persists the
      // recovered draft to the server (lastSavedSnapshotRef points to the
      // server-loaded state, not the merged one).
      lastSavedSnapshotRef.current = JSON.stringify(c);
      initialLoadRef.current = false;
      if (recovered) {
        toast("Modifiche ripristinate", {
          description: "Le ultime modifiche non salvate sono state recuperate. Salvataggio in corso…",
          duration: 4500,
        });
      }
      // Refresh global leads counter — backend just marked this client as seen
      // (if it was an unread lead). Keeps the sidebar badge & dashboard banner in sync.
      try { refreshLeads(); } catch {/* noop */}
    }).catch(() => {
      toast.error("Cliente non trovato");
      navigate("/app/clients");
    });
  }, [id, navigate, refreshLeads]);

  const ensureDefaults = (c) => ({
    ...c,
    status: normalizeStatus(c.status),
    nationality: c.nationality || "",
    language: c.language || "it",
    ceremony_time: c.ceremony_time || "",
    bride_exit_time: c.bride_exit_time || "",
    work_start_time: c.work_start_time || "",
    people_count: c.people_count == null ? 1 : c.people_count,
    event_notes: c.event_notes || "",
    prep_venue: c.prep_venue || "",
    timeline: Array.isArray(c.timeline) ? c.timeline : [],
    makeup: c.makeup || {},
    hair: c.hair || {},
    billing: c.billing || null,
    quote: c.quote
      ? {
          tax_mode: (c.quote.tax_mode || (Number(c.quote.tax) === 0 ? "forfettario" : "standard")),
          ...c.quote,
          quick_notes: Array.isArray(c.quote.quick_notes) ? c.quote.quick_notes : [],
        }
      : {
          items: [], subtotal: 0, discount_total: 0, tax: 22, tax_mode: "standard",
          total: 0, deposit: 0, deposits: [], balance: 0, status: "bozza", notes: "", quick_notes: [],
        },
    contract: c.contract || {
      status: "non_inviato",
      // Conditions and payment_terms left empty here — backend ClientContract supplies the
      // defaults at server-side. The 'Ripristina template' button can refetch on demand.
      conditions: "",
      payment_terms: "",
    },
  });

  // Recalculate totals: line discount → subtotal → general discount → IVA → total.
  // Items flagged `is_optional` are extras-on-request and contribute to totals
  // ONLY when the client has explicitly accepted them on the public page
  // (`client_decision === "accepted"`). Pending/declined optionals are excluded.
  // This mirrors `_is_quote_item_included` / `_quote_item_decision` in server.py
  // so both surfaces stay in sync.
  //
  // Multi-deposit aware: the legacy single `deposit` field is kept in sync
  // with `sum(deposits.amount)` so every other consumer (status bar, server
  // payloads, legacy PDFs) keeps reading the right total without changes.
  const sumDeposits = (q) => {
    const deps = Array.isArray(q.deposits) ? q.deposits : [];
    if (deps.length > 0) {
      return deps.reduce((s, d) => s + (parseFloat(d?.amount) || 0), 0);
    }
    return parseFloat(q.deposit) || 0;
  };

  const recalcQuote = (q) => {
    const subtotal = (q.items || []).reduce((s, l) => {
      if (l.is_optional) {
        // Prefer the new 3-state field; fall back to legacy boolean for older docs.
        const decision = l.client_decision || (l.client_selected ? "accepted" : "pending");
        if (decision !== "accepted") return s;
      }
      const qty = parseFloat(l.qty) || 0;
      const price = parseFloat(l.price) || 0;
      const disc = parseFloat(l.discount) || 0;
      return s + qty * price * (1 - disc / 100);
    }, 0);
    const discountTotal = parseFloat(q.discount_total) || 0;
    const afterDiscount = subtotal * (1 - discountTotal / 100);
    const tax = parseFloat(q.tax) || 0;
    const total = afterDiscount * (1 + tax / 100);
    const deposit = sumDeposits(q);
    const manuallySettled = Boolean(q.manually_settled);
    // Manual settlement override: when the operator has flagged the quote
    // as administratively closed, force balance=0 in the UI so all the
    // surfaces (preventivo header, contratto card, dashboards) stay in
    // sync. Historical deposits are preserved and still visible.
    const balance = manuallySettled ? 0 : Math.max(total - deposit, 0);
    return { ...q, subtotal, total, deposit, balance, manually_settled: manuallySettled };
  };

  // Track the last surfaced error message so we don't spam the user with
  // identical toasts on every debounced autosave attempt (e.g. a plan-403
  // firing on every keystroke). Cleared on successful save.
  const lastErrorMessageRef = useRef("");

  // Single source of truth for persistence — used by autosave AND the manual button.
  // `silent` skips the success toast (autosave) and the busy spinner.
  const persist = useCallback(async ({ silent = false } = {}) => {
    if (!client) return null;
    if (inFlightRef.current) return null;       // Avoid concurrent PUTs
    inFlightRef.current = true;
    if (!silent) setBusy(true);
    if (silent) setAutosaveStatus("saving");
    try {
      const payload = { ...client, budget: parseFloat(client.budget) || 0 };
      payload.quote = recalcQuote(payload.quote || {});
      delete payload.id; delete payload.owner_id; delete payload.created_at; delete payload.updated_at;
      const { data } = await api.put(`/clients/${id}`, payload);
      const next = ensureDefaults(data);
      setClient(next);
      lastSavedSnapshotRef.current = JSON.stringify(next);
      lastErrorMessageRef.current = "";  // clear on success
      if (silent) {
        setAutosaveStatus("saved");
        // Auto-revert badge to idle after a few seconds
        setTimeout(() => setAutosaveStatus((s) => (s === "saved" ? "idle" : s)), 3500);
      } else {
        toast.success("Modifiche salvate");
        setAutosaveStatus("idle");
      }
      return next;
    } catch (err) {
      const detailMsg = formatApiError(err.response?.data?.detail) || err.message || "";
      if (silent) {
        setAutosaveStatus("error");
        // Surface the REAL reason the first time (or when the reason changes).
        // Subsequent identical failures within the same session stay silent —
        // the red badge + tooltip are enough, we don't want spam on every
        // keystroke.
        if (detailMsg && detailMsg !== lastErrorMessageRef.current) {
          lastErrorMessageRef.current = detailMsg;
          toast.error(detailMsg);
        }
      } else {
        lastErrorMessageRef.current = detailMsg;
        toast.error(detailMsg);
      }
      return null;
    } finally {
      inFlightRef.current = false;
      if (!silent) setBusy(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client, id]);

  const save = useCallback(() => persist({ silent: false }), [persist]);

  // ----- Autosave: debounced PUT 2s after the last edit -----
  useEffect(() => {
    if (!client || initialLoadRef.current) return;
    const currentSnapshot = JSON.stringify(client);
    if (currentSnapshot === lastSavedSnapshotRef.current) return;  // not dirty
    // Cancel any pending timer and start a fresh one (debounce reset on each keystroke)
    if (autosaveTimerRef.current) clearTimeout(autosaveTimerRef.current);
    autosaveTimerRef.current = setTimeout(() => {
      // Re-check dirty at fire time (state may have already been saved manually meanwhile)
      if (JSON.stringify(client) !== lastSavedSnapshotRef.current) {
        persist({ silent: true });
      }
    }, 2000);
    return () => { if (autosaveTimerRef.current) clearTimeout(autosaveTimerRef.current); };
  }, [client, persist]);

  // ----- beforeunload guard: warn the user if leaving with unsaved edits -----
  useEffect(() => {
    const handler = (e) => {
      if (!client) return;
      if (JSON.stringify(client) === lastSavedSnapshotRef.current) return;
      if (autosaveStatus === "saving") {
        e.preventDefault();
        e.returnValue = "Salvataggio in corso… vuoi davvero uscire?";
        return e.returnValue;
      }
      if (autosaveStatus === "error" || JSON.stringify(client) !== lastSavedSnapshotRef.current) {
        e.preventDefault();
        e.returnValue = "Ci sono modifiche non salvate. Vuoi uscire?";
        return e.returnValue;
      }
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [client, autosaveStatus]);

  const remove = async () => {
    try {
      await api.delete(`/clients/${id}`);
      toast.success("Cliente eliminato");
      navigate("/app/clients");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  const openPdf = async (kind) => {
    try {
      // Save first to persist latest data, then fetch
      await save();
      const res = await api.get(`/clients/${id}/${kind}/pdf`, { responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      // Trigger download via anchor (works around popup blockers; bypasses cookies issue)
      const filename = `${kind === "quote" ? "preventivo" : "contratto"}-${(client.name || "cliente").replace(/[\s/\\]+/g, "_")}.pdf`;
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      // Also open in a new tab for preview
      try { window.open(url, "_blank"); } catch {}
      setTimeout(() => window.URL.revokeObjectURL(url), 60000);
      toast.success(kind === "quote" ? "Preventivo PDF scaricato" : "Contratto PDF scaricato");
    } catch (err) {
      // Try to read error message from blob (axios returns blob even for errors with responseType:blob)
      let detail = err.message || "Errore nella generazione del PDF";
      if (err.response?.data instanceof Blob) {
        try {
          const text = await err.response.data.text();
          const parsed = JSON.parse(text);
          detail = formatApiError(parsed.detail) || detail;
        } catch {}
      } else if (err.response?.data?.detail) {
        detail = formatApiError(err.response.data.detail);
      }
      toast.error(detail);
    }
  };

  // Fetch the appropriate PDF as a Blob based on the operator's checkbox selection:
  //   • both quote + contract → merged PDF
  //   • only quote → quote PDF
  //   • only contract → contract PDF
  const fetchMergedPdf = async () => {
    let path;
    if (sendQuote && sendContract) {
      path = `/clients/${id}/quote-contract/pdf`;
    } else if (sendContract) {
      path = `/clients/${id}/contract/pdf`;
    } else {
      path = `/clients/${id}/quote/pdf`;
    }
    const res = await api.get(path, { responseType: "blob" });
    return new Blob([res.data], { type: "application/pdf" });
  };

  const buildPdfFilename = () => {
    const safeName = (client?.name || "cliente").replace(/[\s/\\]+/g, "_");
    if (sendQuote && sendContract) return `Preventivo + Contratto - ${safeName}.pdf`;
    if (sendContract) return `Contratto - ${safeName}.pdf`;
    return `Preventivo - ${safeName}.pdf`;
  };

  // Mark quote and/or contract as 'inviato' on the backend AFTER a confirmed
  // share action. The `method` parameter is what makes the Comunicazioni
  // widget show "Inviato via Email / WhatsApp / Link" and records the event
  // in the communications collection.
  //
  // CRITICAL (Feb 2026): we previously called this on dialog open to be
  // resilient against iOS Safari clipboard failures. The product spec was
  // revised: the badge must reflect a REAL send, not just intent. So each
  // share handler now invokes `markAsSent` only on its own success path —
  // email after Resend ACK (server-side), whatsapp after window.open,
  // link after a successful clipboard write.
  const markAsSent = async (method, includeQuote = sendQuote, includeContract = sendContract) => {
    if (!includeQuote && !includeContract) return null;
    try {
      const { data } = await api.post(`/clients/${id}/quote-contract/confirm-sent`, {
        method,
        include_quote: includeQuote,
        include_contract: includeContract,
      });
      setClient((c) => ensureDefaults({
        ...c,
        quote: data.quote || c?.quote,
        contract: data.contract || c?.contract,
        status: c?.status === "lead" ? "preventivo_inviato" : c?.status,
      }));
      return data;
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(formatApiError(detail) || err.message || "Errore aggiornamento stato invio");
      throw err;
    }
  };

  // Open the share modal: save first, then prefetch the right PDF.
  // We NO LONGER mark as sent here — the badge must reflect a real
  // delivery, not just the operator opening the dialog (Feb 2026 spec).
  const openShareDialog = async () => {
    try {
      await save();
    } catch {
      // save() already toasts; continue anyway so the user can still try
    }
    setMergedPdfBlob(null);
    setShareOpen(true);
    setPreparingPdf(true);
    try {
      // Ensure share token + fetch PDF in parallel. Status stays untouched
      // until one of the share handlers below confirms a real send.
      const [, blob] = await Promise.all([
        ensureClientShareToken(),
        fetchMergedPdf(),
      ]);
      setMergedPdfBlob(blob);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message || "Errore generazione PDF");
    } finally {
      setPreparingPdf(false);
    }
  };

  const ensureClientShareToken = async () => {
    if (clientShareToken) return clientShareToken;
    const { data } = await api.post(`/clients/${id}/quote-contract/share`);
    setClientShareToken(data.token);
    return data.token;
  };

  const copyClientLink = async () => {
    setShareBusyAction("link");
    try {
      const t = await ensureClientShareToken();
      const url = `${window.location.origin}/cliente/${t}`;
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(url);
      } else {
        const input = document.createElement("input");
        input.value = url;
        document.body.appendChild(input);
        input.select();
        document.execCommand("copy");
        input.remove();
      }
      // Clipboard write succeeded → register a confirmed "link" send.
      // Errors are non-fatal: the link is on the operator's clipboard
      // even if the backend mark fails (we already toasted the success).
      await markAsSent("link").catch(() => null);
      toast.success("Link cliente copiato");
      setShareOpen(false);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message || "Impossibile copiare");
    } finally {
      setShareBusyAction("");
    }
  };

  const ensurePdfBlob = async () => {
    if (mergedPdfBlob) return mergedPdfBlob;
    const blob = await fetchMergedPdf();
    setMergedPdfBlob(blob);
    return blob;
  };

  const downloadMergedPdf = async () => {
    setShareBusyAction("download");
    try {
      const blob = await ensurePdfBlob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = buildPdfFilename();
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => window.URL.revokeObjectURL(url), 60000);
      toast.success("PDF scaricato");
      setShareOpen(false);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setShareBusyAction("");
    }
  };

  // Send the email AUTOMATICALLY via the backend (Resend provider).
  // No more mailto: — the operator never leaves the app.
  const shareViaEmail = async () => {
    if (!client?.email) {
      toast.error("Email cliente mancante. Aggiungila nella scheda cliente prima di inviare.");
      return;
    }
    setShareBusyAction("email");
    try {
      const { data } = await api.post(`/clients/${id}/quote-contract/email`, {
        include_quote: sendQuote,
        include_contract: sendContract,
        to: client.email,
        // Send the operator's current origin so the email link points to the
        // SAME instance (preview vs production) — avoids "link non valido"
        // when the operator works on preview but the email points to prod DB.
        app_origin: window.location.origin,
      });
      if (data?.demo) {
        toast.warning(`Email simulata (modalità demo). Destinatario: ${data.to}`);
      } else {
        toast.success(`Email inviata a ${data?.to || client.email}`);
      }
      setShareOpen(false);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message || "Invio email fallito");
    } finally {
      setShareBusyAction("");
    }
  };

  // Open WhatsApp web with prefilled message; PDF auto-downloaded for manual attach
  const shareViaWhatsApp = async () => {
    setShareBusyAction("whatsapp");
    try {
      const blob = await ensurePdfBlob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = buildPdfFilename();
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => window.URL.revokeObjectURL(url), 60000);

      const firstName = (client?.name || "").split(" ")[0] || "";
      const link = clientShareToken ? `${window.location.origin}/cliente/${clientShareToken}` : "";
      const msg = encodeURIComponent(
        `Gentile ${firstName},\n` +
        `in allegato trova il preventivo e il contratto relativi al servizio matrimonio.\n` +
        (link ? `\nPuoi visualizzarli e accettarli direttamente qui:\n${link}\n` : "") +
        `\nResto a disposizione per qualsiasi chiarimento.`
      );
      const cleanPhone = (client?.phone || "").replace(/[^\d]/g, "");
      const waUrl = cleanPhone ? `https://wa.me/${cleanPhone}?text=${msg}` : `https://wa.me/?text=${msg}`;
      window.open(waUrl, "_blank", "noopener");
      // WhatsApp window was opened — register the confirmed send. We cannot
      // track delivery for WhatsApp (no API for personal accounts), so the
      // event lifecycle stops at "sent".
      await markAsSent("whatsapp").catch(() => null);
      toast.success("WhatsApp aperto · PDF scaricato per allegato");
      setShareOpen(false);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setShareBusyAction("");
    }
  };

  // Native Web Share API — can attach the PDF on supported devices (iOS/Android)
  const shareViaSystem = async () => {
    setShareBusyAction("system");
    try {
      const blob = await ensurePdfBlob();
      const file = new File([blob], buildPdfFilename(), { type: "application/pdf" });
      const firstName = (client?.name || "").split(" ")[0] || "";
      const shareData = {
        title: `Preventivo + Contratto · ${client?.name || ""}`,
        text:
          `Gentile ${firstName},\n` +
          `in allegato il preventivo e il contratto relativi al servizio matrimonio.`,
        files: [file],
      };
      const canShareFiles = typeof navigator !== "undefined"
        && typeof navigator.canShare === "function"
        && navigator.canShare({ files: [file] });
      if (canShareFiles && typeof navigator.share === "function") {
        await navigator.share(shareData);
        await markAsSent("link").catch(() => null);
        toast.success("Condivisione completata");
      } else if (typeof navigator !== "undefined" && typeof navigator.share === "function") {
        // Fallback: share text only (no files supported on this browser)
        await navigator.share({ title: shareData.title, text: shareData.text });
        await markAsSent("link").catch(() => null);
        toast.info("File non condivisibile da questo browser — condiviso solo il testo");
      } else {
        // Final fallback: download
        await downloadMergedPdf();
        return;
      }
      setShareOpen(false);
    } catch (err) {
      if (err?.name !== "AbortError") {
        toast.error(formatApiError(err.response?.data?.detail) || err.message || "Condivisione non disponibile");
      }
    } finally {
      setShareBusyAction("");
    }
  };

  const canWebShare =
    typeof navigator !== "undefined" && typeof navigator.share === "function";

  if (!client) return <div className="label-uppercase">Caricamento…</div>;

  const patch = (k, v) => setClient({ ...client, [k]: v });
  const patchMakeup = (k, v) => setClient({ ...client, makeup: { ...(client.makeup || {}), [k]: v } });
  const patchHair = (k, v) => setClient({ ...client, hair: { ...(client.hair || {}), [k]: v } });
  const patchQuote = (updates) => {
    const next = recalcQuote({ ...(client.quote || {}), ...updates });
    setClient({ ...client, quote: next });
  };
  const patchContract = (k, v) =>
    setClient({ ...client, contract: { ...(client.contract || {}), [k]: v } });

  const setContractMode = async (mode) => {
    if (!mode || mode === (client.contract?.mode || "standard")) return;
    try {
      const { data } = await api.get(`/contract/template?mode=${mode}`);
      setClient({
        ...client,
        contract: {
          ...(client.contract || {}),
          mode,
          conditions: data.conditions || "",
          payment_terms: data.payment_terms || "",
        },
      });
      toast.success("Contratto aggiornato automaticamente");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  const addQuoteItem = () =>
    patchQuote({ items: [...(client.quote.items || []), { desc: "", qty: 1, price: 0 }] });
  const updateQuoteItem = (i, k, v) => {
    const items = [...(client.quote.items || [])];
    items[i] = { ...items[i], [k]: v };
    patchQuote({ items });
  };
  const removeQuoteItem = (i) => {
    const items = (client.quote.items || []).filter((_, j) => j !== i);
    patchQuote({ items });
  };

  const mapsUrl = client.venue
    ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(client.venue)}`
    : null;
  const prepMapsUrl = client.prep_venue
    ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(client.prep_venue)}`
    : null;

  const quoteStatusColor = {
    bozza: "bg-secondary text-neutral-700",
    inviato: "bg-blue-100 text-blue-700 border-blue-200",
    accettato: "bg-primary/15 text-primary border-primary/30",
    rifiutato: "bg-red-100 text-red-700 border-red-200",
  }[client.quote?.status] || "bg-secondary";
  const contractStatusColor = {
    non_inviato: "bg-secondary text-neutral-700",
    inviato: "bg-blue-100 text-blue-700 border-blue-200",
    firmato: "bg-primary/15 text-primary border-primary/30",
  }[client.contract?.status] || "bg-secondary";

  return (
    <div className="space-y-8" data-testid="client-detail-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <Link to="/app/clients" className="text-sm text-neutral-600 hover:text-primary flex items-center gap-1" data-testid="back-to-clients">
          <ArrowLeft className="w-4 h-4" strokeWidth={1.5} /> Torna ai clienti
        </Link>
        <div className="flex gap-3 flex-wrap items-center">
          <div className="flex items-center gap-3 bg-white border border-border rounded-sm px-3 py-2" data-testid="send-options-group">
            <label className="flex items-center gap-2 text-xs cursor-pointer select-none">
              <Checkbox
                checked={sendQuote}
                onCheckedChange={(v) => setSendQuote(!!v)}
                data-testid="send-include-quote-checkbox"
              />
              <span>Invia preventivo</span>
            </label>
            <label className="flex items-center gap-2 text-xs cursor-pointer select-none">
              <Checkbox
                checked={sendContract}
                onCheckedChange={(v) => setSendContract(!!v)}
                data-testid="send-include-contract-checkbox"
              />
              <span>Invia contratto</span>
            </label>
          </div>
          <Button
            onClick={openShareDialog}
            disabled={preparingPdf || (!sendQuote && !sendContract)}
            variant="outline"
            className="rounded-sm border-primary text-primary hover:bg-primary hover:text-primary-foreground disabled:opacity-50"
            data-testid="send-quote-contract-button"
          >
            <Send className="w-4 h-4 mr-2" strokeWidth={1.5} />
            Invia
          </Button>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="outline" className="rounded-sm text-destructive border-destructive/30 hover:bg-destructive/5" data-testid="client-delete-button">
                <Trash2 className="w-4 h-4 mr-2" strokeWidth={1.5} /> Elimina
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent className="rounded-sm">
              <AlertDialogHeader>
                <AlertDialogTitle>Eliminare cliente?</AlertDialogTitle>
                <AlertDialogDescription>L'operazione è irreversibile. Tutti i dati associati andranno persi.</AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel className="rounded-sm">Annulla</AlertDialogCancel>
                <AlertDialogAction onClick={remove} className="rounded-sm bg-destructive hover:bg-destructive/90" data-testid="client-delete-confirm">Elimina</AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
          <Button onClick={save} disabled={busy} className="rounded-sm" data-testid="client-save-detail-button">
            <Save className="w-4 h-4 mr-2" strokeWidth={1.5} /> {busy ? "Salvataggio…" : "Salva"}
          </Button>
        </div>
      </div>

      {/* Autosave status indicator */}
      <AutosaveBadge status={autosaveStatus} dirty={client && JSON.stringify(client) !== lastSavedSnapshotRef.current} />

      <div className="flex items-start gap-4 sm:gap-6 md:gap-8">
        {/* Hero portrait — profile_image → cover_image → photos[0]
            o iniziali (champagne-gold). Dimensione "hero": 100→130→160 px. */}
        <ClientAvatar
          client={client}
          size="hero"
          onUpload={async (dataUrl, validationErr) => {
            if (validationErr) {
              toast.error(validationErr.message || "File non valido");
              return;
            }
            try {
              const { data } = await api.put(`/clients/${id}/profile-image`, { data: dataUrl });
              // Sostituiamo solo il campo immagine così non perdiamo edit
              // pendenti in altri campi del form (autosave a parte).
              setClient((prev) => ({ ...prev, profile_image: data?.profile_image || "" }));
              // Snapshot autosave: l'upload è già persistito, allineiamo
              // il ref così l'AutosaveBadge non lampeggia "dirty".
              if (lastSavedSnapshotRef?.current) {
                try {
                  const snap = JSON.parse(lastSavedSnapshotRef.current);
                  snap.profile_image = data?.profile_image || "";
                  lastSavedSnapshotRef.current = JSON.stringify(snap);
                } catch { /* tolerant */ }
              }
              toast.success("Foto aggiornata con successo");
            } catch (err) {
              toast.error(formatApiError(err.response?.data?.detail) || err.message || "Errore durante il caricamento");
              throw err;
            }
          }}
        />
        <div className="min-w-0 flex-1">
          <span className={`label-uppercase px-2 py-1 rounded-sm border inline-block ${clientBadge(client).className}`} data-testid="client-detail-badge">
            {clientBadge(client).label}
          </span>
          {/* H1 ridimensionato per mobile (Feb 2026 C2 sprint):
              su < md riduciamo da 5xl → 3xl così l'hero non occupa
              metà schermata su iPhone, lasciando spazio per i contatti
              rapidi inline sotto. Desktop md+ resta 5xl invariato. */}
          <h1 className="font-serif-heading text-3xl md:text-5xl tracking-tight mt-2 break-words">{client.name}</h1>
          {client.partner_name && <p className="text-neutral-500 mt-1 text-sm md:text-base">& {client.partner_name}</p>}
          <ClientStatusBar client={client} hasMakeup={hasMakeup} hasHair={hasHair} />
        </div>
      </div>

      {/* Mobile-only quick contacts row (Feb 2026 C2 sprint).
          Sits between the hero and the tab bar so the operator can
          call/WhatsApp/email the sposa in 1 tap no matter which tab
          is currently selected. Desktop has the same affordances
          inside the Wedding tab + status bar, so we only render this
          on mobile to avoid duplication. */}
      <div className="md:hidden">
        <ContactsQuickRow phone={client.phone} email={client.email} name={client.name} />
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="rounded-sm bg-secondary flex-wrap h-auto gap-0.5">
          {/* Each tab is wrapped with its InfoTip as a SIBLING (not a child)
              because nesting <button> inside Radix' TabsTrigger button leads
              to mousedown event ordering issues that break tab switching.
              The wrapper recreates the visual "icon next to label" look. */}
          <span className="inline-flex items-center bg-transparent rounded-sm">
            <TabsTrigger value="overview" className="rounded-sm rounded-r-none data-[state=active]:bg-white pr-2.5" data-testid="tab-overview">
              <LayoutDashboard className="w-4 h-4 mr-2" strokeWidth={1.5} /> Panoramica
            </TabsTrigger>
          </span>
          <span className="inline-flex items-center bg-transparent rounded-sm">
            <TabsTrigger value="wedding" className="rounded-sm rounded-r-none data-[state=active]:bg-white pr-1.5" data-testid="tab-wedding">
              <HeartHandshake className="w-4 h-4 mr-2" strokeWidth={1.5} /> Wedding
            </TabsTrigger>
            <span className="px-1 py-1.5 rounded-sm rounded-l-none bg-transparent data-[active]:bg-white"><InfoTip {...TIPS.tab_wedding} size="xs" data-testid="tip-tab-wedding" /></span>
          </span>
          {hasMakeup && (
            <span className="inline-flex items-center bg-transparent rounded-sm">
              <TabsTrigger value="makeup" className="rounded-sm rounded-r-none data-[state=active]:bg-white pr-1.5" data-testid="tab-makeup">
                <Sparkles className="w-4 h-4 mr-2" strokeWidth={1.5} /> Makeup
              </TabsTrigger>
              <span className="px-1 py-1.5"><InfoTip {...TIPS.tab_makeup} size="xs" data-testid="tip-tab-makeup" /></span>
            </span>
          )}
          {hasHair && (
            <span className="inline-flex items-center bg-transparent rounded-sm">
              <TabsTrigger value="hair" className="rounded-sm rounded-r-none data-[state=active]:bg-white pr-1.5" data-testid="tab-hair">
                <Scissors className="w-4 h-4 mr-2" strokeWidth={1.5} /> Hair
              </TabsTrigger>
              <span className="px-1 py-1.5"><InfoTip {...TIPS.tab_hair} size="xs" data-testid="tip-tab-hair" /></span>
            </span>
          )}
          <span className="inline-flex items-center bg-transparent rounded-sm">
            <TabsTrigger value="vendors" className="rounded-sm rounded-r-none data-[state=active]:bg-white pr-1.5" data-testid="tab-vendors">
              <Briefcase className="w-4 h-4 mr-2" strokeWidth={1.5} /> Partner / Fornitori
            </TabsTrigger>
            <span className="px-1 py-1.5"><InfoTip {...TIPS.tab_partner} size="xs" data-testid="tip-tab-partner" /></span>
          </span>
          <span className="inline-flex items-center bg-transparent rounded-sm">
            <TabsTrigger value="quote" className="rounded-sm rounded-r-none data-[state=active]:bg-white pr-1.5" data-testid="tab-quote">
              <FileText className="w-4 h-4 mr-2" strokeWidth={1.5} /> Preventivo
            </TabsTrigger>
            <span className="px-1 py-1.5"><InfoTip {...TIPS.tab_quote} size="xs" data-testid="tip-tab-quote" /></span>
          </span>
          <span className="inline-flex items-center bg-transparent rounded-sm">
            <TabsTrigger value="contract" className="rounded-sm rounded-r-none data-[state=active]:bg-white pr-1.5" data-testid="tab-contract">
              <ScrollText className="w-4 h-4 mr-2" strokeWidth={1.5} /> Contratto
            </TabsTrigger>
            <span className="px-1 py-1.5"><InfoTip {...TIPS.tab_contract} size="xs" data-testid="tip-tab-contract" /></span>
          </span>
          <span className="inline-flex items-center bg-transparent rounded-sm">
            <TabsTrigger value="timeline" className="rounded-sm rounded-r-none data-[state=active]:bg-white pr-1.5" data-testid="tab-timeline">
              <Clock className="w-4 h-4 mr-2" strokeWidth={1.5} /> Timeline
            </TabsTrigger>
            <span className="px-1 py-1.5"><InfoTip {...TIPS.tab_timeline} size="xs" data-testid="tip-tab-timeline" /></span>
          </span>
          <span className="inline-flex items-center bg-transparent rounded-sm">
            <TabsTrigger value="wedding-day" className="rounded-sm rounded-r-none data-[state=active]:bg-white pr-1.5" data-testid="tab-wedding-day">
              <CalendarHeart className="w-4 h-4 mr-2" strokeWidth={1.5} /> Wedding Day
            </TabsTrigger>
            <span className="px-1 py-1.5"><InfoTip {...TIPS.tab_wedding_day} size="xs" data-testid="tip-tab-wedding-day" /></span>
          </span>
          <span className="inline-flex items-center bg-transparent rounded-sm">
            <TabsTrigger value="map" className="rounded-sm rounded-r-none data-[state=active]:bg-white pr-1.5" data-testid="tab-map">
              <MapPin className="w-4 h-4 mr-2" strokeWidth={1.5} /> Mappa
            </TabsTrigger>
            <span className="px-1 py-1.5"><InfoTip {...TIPS.tab_map} size="xs" data-testid="tip-tab-map" /></span>
          </span>
        </TabsList>

        <TabsContent value="overview" className="mt-6" data-testid="tab-content-overview">
          <ClientOverview
            client={client}
            hasMakeup={hasMakeup}
            hasHair={hasHair}
            clientShareToken={clientShareToken}
            onNavigate={setActiveTab}
            onResendQuote={openShareDialog}
          />
        </TabsContent>

        <TabsContent value="wedding" className="mt-6">
          {/* ── Mobile (< md): accordion card-stack (C1 sprint Feb 2026).
              Same fields, same callbacks, same business logic — just a
              touch-friendly container. Renders only on mobile so the
              desktop experience below is byte-identical to before. ── */}
          <div className="md:hidden">
            <WeddingMobileAccordion
              client={client}
              patch={patch}
              setClient={setClient}
              NATIONALITIES={NATIONALITIES}
              LANGUAGES={LANGUAGES}
              CLIENT_STATUSES={CLIENT_STATUSES}
              prepMapsUrl={prepMapsUrl}
              mapsUrl={mapsUrl}
              BillingForm={BillingForm}
              validateBilling={validateBilling}
              onSuggestLanguageToast={(langLabel) =>
                toast.info(`Lingua suggerita: ${langLabel}`, { duration: 2500 })
              }
            />
          </div>

          {/* ── Desktop (≥ md): original linear form, completely untouched. ── */}
          <div className="hidden md:block bg-white border border-border rounded-sm p-8 space-y-8">
            {/* Contatti */}
            <section>
              <h2 className="font-serif-heading text-2xl">Contatti</h2>
              <div className="grid md:grid-cols-2 gap-5 mt-5">
                <Field label="Nome sposa" value={client.name} onChange={(v) => patch("name", v)} />
                <Field label="Partner wedding planner" value={client.partner_name || ""} onChange={(v) => patch("partner_name", v)} />
                <Field label="Email" type="email" value={client.email || ""} onChange={(v) => patch("email", v)} />
                <Field label="Telefono" value={client.phone || ""} onChange={(v) => patch("phone", v)} />
              </div>
            </section>

            {/* Nazionalità & Lingua documenti */}
            <section className="border-t border-border pt-6">
              <h2 className="font-serif-heading text-2xl">Nazionalità e lingua</h2>
              <p className="text-xs text-neutral-500 mt-1">
                La lingua selezionata sarà usata per generare preventivo, contratto ed email al cliente.
                La nazionalità è informativa e suggerisce automaticamente la lingua più probabile.
              </p>
              <div className="grid md:grid-cols-2 gap-5 mt-5">
                <div>
                  <Label className="label-uppercase">Nazionalità</Label>
                  <Select
                    value={client.nationality || ""}
                    onValueChange={(v) => {
                      const nat = NATIONALITIES.find((n) => n.code === v);
                      const nextClient = { ...client, nationality: v };
                      // Se la lingua è al default (it) e la nazionalità suggerisce un'altra lingua → aggiorna
                      if (nat?.suggest && (!client.language || client.language === "it") && nat.suggest !== "it") {
                        nextClient.language = nat.suggest;
                        const langLabel = LANGUAGES.find((l) => l.v === nat.suggest)?.label || nat.suggest;
                        toast.info(`Lingua suggerita: ${langLabel}`, { duration: 2500 });
                      }
                      setClient(nextClient);
                    }}
                  >
                    <SelectTrigger className="mt-1 rounded-sm" data-testid="wedding-nationality-select">
                      <SelectValue placeholder="Seleziona nazionalità" />
                    </SelectTrigger>
                    <SelectContent>
                      {NATIONALITIES.map((n) => (
                        <SelectItem key={n.code} value={n.code}>{n.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="label-uppercase">Lingua documenti</Label>
                  <Select
                    value={client.language || "it"}
                    onValueChange={(v) => patch("language", v)}
                  >
                    <SelectTrigger className="mt-1 rounded-sm" data-testid="wedding-language-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {LANGUAGES.map((l) => (
                        <SelectItem key={l.v} value={l.v}>{l.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </section>

            {/* Evento - data e orari */}
            <section className="border-t border-border pt-6">
              <h2 className="font-serif-heading text-2xl">Evento</h2>
              <div className="grid md:grid-cols-2 gap-5 mt-5">
                <Field
                  label="Data matrimonio"
                  type="date"
                  value={client.wedding_date || ""}
                  onChange={(v) => patch("wedding_date", v)}
                  testid="wedding-date-input"
                />
                <Field
                  label="Persone da preparare"
                  type="number"
                  value={client.people_count ?? 1}
                  onChange={(v) => patch("people_count", parseInt(v, 10) || 0)}
                  testid="wedding-people-count-input"
                  numberOpts={{ allowDecimals: false, min: 0 }}
                />
              </div>
              <div className="grid md:grid-cols-3 gap-5 mt-5">
                <TimePickerField
                  label="Orario cerimonia"
                  value={client.ceremony_time || ""}
                  onChange={(v) => patch("ceremony_time", v)}
                  testid="wedding-ceremony-time-input"
                />
                <TimePickerField
                  label="Orario uscita sposa"
                  value={client.bride_exit_time || ""}
                  onChange={(v) => patch("bride_exit_time", v)}
                  testid="wedding-bride-exit-time-input"
                />
                <TimePickerField
                  label="Orario inizio lavoro"
                  value={client.work_start_time || ""}
                  onChange={(v) => patch("work_start_time", v)}
                  testid="wedding-work-start-input"
                />
              </div>
              <div className="grid md:grid-cols-2 gap-5 mt-5">
                <Field
                  label="Budget (€)"
                  type="number"
                  value={client.budget || 0}
                  onChange={(v) => patch("budget", v)}
                  numberOpts={{ allowDecimals: true, min: 0 }}
                />
                <div>
                  <Label className="label-uppercase">Stato cliente</Label>
                  <Select value={client.status} onValueChange={(v) => patch("status", v)}>
                    <SelectTrigger className="mt-1 rounded-sm" data-testid="wedding-status-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {CLIENT_STATUSES.map((s) => (
                        <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </section>

            {/* Logistica - Prep + Cerimonia */}
            <section className="border-t border-border pt-6 space-y-5">
              <h2 className="font-serif-heading text-2xl">Logistica</h2>
              <div className="flex items-end justify-between gap-4 flex-wrap">
                <div className="flex-1 min-w-[260px]">
                  <Label className="label-uppercase">Luogo preparazione</Label>
                  <Input
                    value={client.prep_venue || ""}
                    onChange={(e) => patch("prep_venue", e.target.value)}
                    className="mt-1 rounded-sm"
                    placeholder="Es. Casa della sposa, Hotel Continental, Via Verdi 5"
                    data-testid="wedding-prep-venue-input"
                  />
                  <p className="text-xs text-neutral-500 mt-1">Dove ti rechi per il make-up/hair (spesso diverso dalla cerimonia).</p>
                </div>
                <a
                  href={prepMapsUrl || "#"}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => { if (!prepMapsUrl) e.preventDefault(); }}
                  data-testid="wedding-open-prep-maps-button"
                >
                  <Button
                    type="button"
                    variant="outline"
                    className="rounded-sm border-primary text-primary hover:bg-primary hover:text-primary-foreground"
                    disabled={!prepMapsUrl}
                  >
                    <ExternalLink className="w-4 h-4 mr-2" strokeWidth={1.5} />
                    Apri preparazione
                  </Button>
                </a>
              </div>

              <div className="flex items-end justify-between gap-4 flex-wrap">
                <div className="flex-1 min-w-[260px]">
                  <Label className="label-uppercase">Location cerimonia</Label>
                  <Input
                    value={client.venue || ""}
                    onChange={(e) => patch("venue", e.target.value)}
                    className="mt-1 rounded-sm"
                    placeholder="Es. Villa dei Cedri, Via Roma 12, Firenze"
                    data-testid="wedding-venue-input"
                  />
                </div>
                <a
                  href={mapsUrl || "#"}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => { if (!mapsUrl) e.preventDefault(); }}
                  data-testid="wedding-open-maps-button"
                >
                  <Button
                    type="button"
                    variant="outline"
                    className="rounded-sm border-primary text-primary hover:bg-primary hover:text-primary-foreground"
                    disabled={!mapsUrl}
                  >
                    <ExternalLink className="w-4 h-4 mr-2" strokeWidth={1.5} />
                    Apri cerimonia
                  </Button>
                </a>
              </div>
            </section>

            {/* Note evento */}
            <section className="border-t border-border pt-6">
              <Label className="label-uppercase">Note evento</Label>
              <Textarea
                rows={5}
                value={client.event_notes || ""}
                onChange={(e) => patch("event_notes", e.target.value)}
                className="mt-1 rounded-sm"
                placeholder="Indicazioni operative per il giorno: ordine di preparazione, contatti del coordinatore, dress-code, ecc."
                data-testid="wedding-event-notes-textarea"
              />
            </section>

            {/* Dati fatturazione cliente — facoltativo in creazione, obbligatorio prima della firma OTP */}
            <section className="border-t border-border pt-6" data-testid="client-billing-section">
              <div className="flex items-start justify-between flex-wrap gap-3">
                <div>
                  <h2 className="font-serif-heading text-2xl flex items-center gap-2">
                    <Receipt className="w-5 h-5 text-primary" strokeWidth={1.5} />
                    Dati fatturazione cliente
                  </h2>
                  <p className="text-sm text-neutral-500 mt-1">
                    <span className="font-medium text-neutral-700">Facoltativi.</span> Servono per la fattura finale al cliente
                    (li potrai aggiungere anche dopo la firma del contratto). Preventivo, contratto e firma elettronica si inviano regolarmente anche senza questi dati.
                  </p>
                </div>
                {(() => {
                  const err = validateBilling(client.billing);
                  if (!client.billing || !client.billing.type) {
                    return (
                      <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs bg-neutral-50 text-neutral-500 border border-neutral-200" data-testid="client-billing-status-empty">
                        Facoltativi · Da compilare
                      </span>
                    );
                  }
                  if (err) {
                    return (
                      <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs bg-neutral-50 text-neutral-600 border border-neutral-200" data-testid="client-billing-status-incomplete">
                        <AlertCircle className="w-3.5 h-3.5" strokeWidth={1.5} /> Da completare (facoltativo)
                      </span>
                    );
                  }
                  return (
                    <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs bg-emerald-50 text-emerald-700 border border-emerald-200" data-testid="client-billing-status-complete">
                      <CheckCircle2 className="w-3.5 h-3.5" strokeWidth={1.5} /> Completi
                    </span>
                  );
                })()}
              </div>
              <div className="mt-5">
                <BillingForm
                  value={client.billing}
                  onChange={(v) => patch("billing", v)}
                  scope="client"
                />
              </div>
              {client.billing?.type && (() => {
                const err = validateBilling(client.billing);
                return err ? (
                  <p className="mt-4 text-xs text-neutral-600 bg-neutral-50 border border-neutral-200 rounded-sm p-3">
                    {err}. <span className="text-neutral-500">Puoi compilare i dati anche più avanti — non bloccano l'invio del preventivo né la firma del contratto.</span>
                  </p>
                ) : null;
              })()}
            </section>

            {/* Note generali (CRM) */}
            <section className="border-t border-border pt-6">
              <Label className="label-uppercase">Note generali (interne)</Label>
              <Textarea
                rows={3}
                value={client.notes || ""}
                onChange={(e) => patch("notes", e.target.value)}
                className="mt-1 rounded-sm"
                placeholder="Promemoria interni, follow-up, preferenze generali..."
              />
            </section>
          </div>
        </TabsContent>

        {hasMakeup && (
          <TabsContent value="makeup" className="mt-6">
            <MakeupTab client={client} setClient={setClient} team={team} id={id} patch={patch} />
          </TabsContent>
        )}

        {hasHair && (
          <TabsContent value="hair" className="mt-6">
            <HairTab client={client} setClient={setClient} team={team} id={id} patch={patch} />
          </TabsContent>
        )}

        {/* Partner / Fornitori — INTERNAL ONLY. Never exposed to the client
            (not in the public link, the quote PDF, the contract PDF, nor any
            outbound email). The whitelisted public_client endpoint and PDF
            builder do not reference `client.vendors`, so the data stays local
            to the operator's workspace. */}
        <TabsContent value="vendors" className="mt-6">
          <VendorsSection client={client} patch={patch} team={team} />
        </TabsContent>

        <TabsContent value="quote" className="mt-6">
          <div className="bg-white border border-border rounded-sm p-4 md:p-8 space-y-6 pb-32 md:pb-8" data-testid="quote-tab-container">
            <div className="flex items-start justify-between flex-wrap gap-3">
              <div>
                <h2 className="font-serif-heading text-2xl">Preventivo</h2>
                <p className="text-sm text-neutral-500 mt-1">Servizi, totali, acconto e saldo.</p>
              </div>
              <div className="flex items-center gap-3 flex-wrap">
                <Badge className={`rounded-sm border ${quoteStatusColor}`} data-testid="quote-status-badge">
                  {QUOTE_STATUSES.find((s) => s.v === client.quote.status)?.label || client.quote.status}
                </Badge>
                <Select value={client.quote.status} onValueChange={(v) => patchQuote({ status: v })}>
                  <SelectTrigger className="w-40 rounded-sm" data-testid="quote-status-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {QUOTE_STATUSES.map((s) => <SelectItem key={s.v} value={s.v}>{s.label}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Button variant="outline" className="rounded-sm hidden md:inline-flex" onClick={() => openPdf("quote")} data-testid="quote-pdf-button">
                  <Download className="w-4 h-4 mr-2" strokeWidth={1.5} /> Genera PDF
                </Button>
              </div>
            </div>

            {/* MOBILE: total banner (visible only on small screens) */}
            <div className="md:hidden -mt-2">
              <QuoteTotalBanner quote={client.quote} />
            </div>

            <div>
              <Label className="label-uppercase hidden md:block">Servizi</Label>
              <datalist id="quote-service-options">
                {QUOTE_SERVICE_OPTIONS.map((opt) => (
                  <option key={opt} value={opt} />
                ))}
              </datalist>

              {/* MOBILE: card-stack editor (replaces the grid-12 table below md) */}
              <QuoteItemsMobile
                items={client.quote.items || []}
                serviceOptions={QUOTE_SERVICE_OPTIONS}
                customSentinel={QUOTE_SERVICE_CUSTOM_SENTINEL}
                onAdd={addQuoteItem}
                onUpdate={updateQuoteItem}
                onRemove={removeQuoteItem}
              />

              {/* DESKTOP / TABLET (≥ md): existing grid-12 table kept untouched */}
              <div className="mt-2 space-y-2 hidden md:block">
                <div className="grid grid-cols-12 gap-2 text-xs text-neutral-500 px-1">
                  <div className="col-span-5">Descrizione</div>
                  <div className="col-span-1 text-right">Qt</div>
                  <div className="col-span-2 text-right">Prezzo</div>
                  <div className="col-span-2 text-right">Sconto %</div>
                  <div className="col-span-1 text-right">Totale</div>
                  <div className="col-span-1" />
                </div>
                {(client.quote.items || []).map((l, i) => {
                  const isOptional = !!l.is_optional;
                  // Resolve the 3-state decision with legacy fallback so docs
                  // saved before the migration still display correctly.
                  const decision = isOptional
                    ? (l.client_decision || (l.client_selected ? "accepted" : "pending"))
                    : "accepted";
                  const isAccepted = isOptional && decision === "accepted";
                  const isDeclined = isOptional && decision === "declined";
                  const isPending = isOptional && decision === "pending";
                  const lineTotal =
                    (parseFloat(l.qty) || 0) *
                    (parseFloat(l.price) || 0) *
                    (1 - (parseFloat(l.discount) || 0) / 100);
                  const wrapperClass = isAccepted
                    ? "bg-emerald-50/60 border border-emerald-200/70 px-2 py-2"
                    : isDeclined
                      ? "bg-neutral-100/70 border border-neutral-200 px-2 py-2 opacity-70"
                      : isPending
                        ? "bg-amber-50/60 border border-amber-200/70 px-2 py-2"
                        : "";
                  return (
                    <div
                      key={i}
                      className={`rounded-sm transition-colors ${wrapperClass}`}
                      data-testid={`quote-item-wrapper-${i}`}
                    >
                      <div className="grid grid-cols-12 gap-2 items-center" data-testid={`quote-item-${i}`}>
                        <Input
                          list="quote-service-options"
                          className={`col-span-5 rounded-sm ${isOptional ? "text-neutral-600 italic" : ""} ${isDeclined ? "line-through text-neutral-400" : ""}`}
                          placeholder="Es. Trucco sposa"
                          value={l.desc}
                          onChange={(e) => {
                            const v = e.target.value;
                            // Sentinel: selecting "Altro (inserisci manualmente)" should clear
                            // the field so the user can type freely without that label sticking.
                            if (v === QUOTE_SERVICE_CUSTOM_SENTINEL) {
                              updateQuoteItem(i, "desc", "");
                            } else {
                              updateQuoteItem(i, "desc", v);
                            }
                          }}
                          data-testid={`quote-item-desc-${i}`}
                        />
                        <NumberField
                          className="col-span-1 rounded-sm text-right"
                          value={l.qty}
                          onChange={(n) => updateQuoteItem(i, "qty", n)}
                          allowDecimals={false}
                          min={0}
                          data-testid={`quote-item-qty-${i}`}
                        />
                        <NumberField
                          className="col-span-2 rounded-sm text-right"
                          value={l.price}
                          onChange={(n) => updateQuoteItem(i, "price", n)}
                          min={0}
                          data-testid={`quote-item-price-${i}`}
                        />
                        <NumberField
                          className="col-span-2 rounded-sm text-right"
                          min={0}
                          max={100}
                          value={l.discount ?? 0}
                          onChange={(n) => updateQuoteItem(i, "discount", n)}
                          data-testid={`quote-item-discount-${i}`}
                        />
                        <div
                          className={`col-span-1 text-right text-sm truncate ${isAccepted ? "font-medium text-emerald-700" : isPending || isDeclined ? "text-neutral-400 line-through" : "font-medium text-neutral-700"}`}
                          data-testid={`quote-item-line-total-${i}`}
                        >
                          {fmtEuro(lineTotal)}
                        </div>
                        <button type="button" onClick={() => removeQuoteItem(i)} className="col-span-1 text-destructive flex items-center justify-center" data-testid={`quote-item-remove-${i}`}>
                          <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                        </button>
                      </div>
                      <div className="mt-1.5 flex items-center gap-2 pl-1 flex-wrap">
                        <Checkbox
                          id={`quote-item-optional-${i}`}
                          checked={isOptional}
                          onCheckedChange={(v) => updateQuoteItem(i, "is_optional", Boolean(v))}
                          data-testid={`quote-item-optional-checkbox-${i}`}
                        />
                        <label
                          htmlFor={`quote-item-optional-${i}`}
                          className="text-xs text-neutral-500 cursor-pointer select-none"
                        >
                          Opzionale — non incluso nel totale
                        </label>
                        {isPending && (
                          <span
                            className="ml-1 inline-flex items-center px-2 py-0.5 rounded-sm text-[10px] uppercase tracking-wide bg-amber-100 text-amber-800 border border-amber-200"
                            data-testid={`quote-item-badge-pending-${i}`}
                          >
                            Opzionale
                          </span>
                        )}
                        {isAccepted && (
                          <span
                            className="ml-1 inline-flex items-center px-2 py-0.5 rounded-sm text-[10px] uppercase tracking-wide bg-emerald-100 text-emerald-800 border border-emerald-200"
                            data-testid={`quote-item-badge-accepted-${i}`}
                          >
                            Confermato dal cliente
                          </span>
                        )}
                        {isDeclined && (
                          <span
                            className="ml-1 inline-flex items-center px-2 py-0.5 rounded-sm text-[10px] uppercase tracking-wide bg-neutral-200 text-neutral-600 border border-neutral-300"
                            data-testid={`quote-item-badge-declined-${i}`}
                          >
                            Declinato dal cliente
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })}
                <Button type="button" variant="outline" size="sm" onClick={addQuoteItem} className="rounded-sm" data-testid="quote-add-item">
                  <Plus className="w-3 h-3 mr-1" strokeWidth={1.5} /> Aggiungi servizio
                </Button>
              </div>
            </div>

            <div className="grid md:grid-cols-5 gap-5 border-t border-border pt-6">
              <div>
                <Label className="label-uppercase">Subtotale</Label>
                <div className="mt-1 h-10 flex items-center font-medium" data-testid="quote-subtotal">{fmtEuro(client.quote.subtotal)}</div>
              </div>
              <div>
                <Label className="label-uppercase">Sconto generale %</Label>
                <NumberField
                  min={0}
                  max={100}
                  value={client.quote.discount_total ?? 0}
                  onChange={(n) => patchQuote({ discount_total: n })}
                  className="mt-1 rounded-sm"
                  data-testid="quote-discount-total-input"
                />
              </div>
              <div>
                <Label className="label-uppercase">IVA</Label>
                <Select
                  value={client.quote.tax_mode || (Number(client.quote.tax) === 0 ? "forfettario" : "standard")}
                  onValueChange={(v) => {
                    // Switching mode: keep the legacy "tax" field in sync so all existing
                    // PDF code / API payloads keep working with no further changes.
                    if (v === "forfettario") {
                      patchQuote({ tax_mode: "forfettario", tax: 0 });
                    } else {
                      patchQuote({ tax_mode: "standard", tax: 22 });
                    }
                  }}
                >
                  <SelectTrigger className="mt-1 rounded-sm" data-testid="quote-tax-mode-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="standard">IVA 22%</SelectItem>
                    <SelectItem value="forfettario">Regime forfettario (senza IVA)</SelectItem>
                  </SelectContent>
                </Select>
                {(client.quote.tax_mode === "forfettario" || Number(client.quote.tax) === 0) && (
                  <p className="text-[11px] text-neutral-500 mt-1" data-testid="quote-forfettario-hint">
                    Importo esente IVA (regime forfettario)
                  </p>
                )}
              </div>
              <div className="md:col-span-2">
                <div className="flex items-baseline justify-between gap-2 mb-1">
                  <Label className="label-uppercase">Acconti ricevuti</Label>
                  <span className="text-[11px] text-neutral-500 tabular-nums" data-testid="quote-deposits-total">
                    Totale acconti: <span className="font-medium text-neutral-700">{fmtEuro(sumDeposits(client.quote))}</span>
                  </span>
                </div>
                <DepositsEditor
                  deposits={client.quote.deposits || []}
                  onChange={(next) => {
                    // Defense-in-depth: coerce every `amount` to a finite
                    // number before we even touch state. Stops the
                    // "Input should be a valid number" Pydantic toast
                    // from ever firing, even if a future code path
                    // injects a stringy amount somewhere upstream.
                    const sanitized = (next || []).map((d) => {
                      const n = Number(d?.amount);
                      return {
                        date: d?.date || "",
                        amount: Number.isFinite(n) ? n : 0,
                        notes: d?.notes || "",
                      };
                    });
                    patchQuote({
                      deposits: sanitized,
                      deposit: sanitized.reduce((s, d) => s + d.amount, 0),
                    });
                  }}
                />
              </div>
              <div>
                <Label className="label-uppercase">Saldo residuo</Label>
                <div className="mt-1 h-10 flex items-center gap-2 font-medium" data-testid="quote-balance">
                  {client.quote.manually_settled ? (
                    <span
                      className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-sm bg-emerald-50 border border-emerald-300 text-emerald-800 text-xs font-bold uppercase tracking-wider"
                      data-testid="quote-balance-settled-badge"
                    >
                      <CheckCircle2 className="w-3.5 h-3.5" strokeWidth={2} />
                      Saldato
                    </span>
                  ) : (
                    <span>{fmtEuro(client.quote.balance)}</span>
                  )}
                </div>
                {/* Manual settlement flag — administrative closure, NOT a real
                    payment. See backend models.ClientQuote.manually_settled. */}
                <label
                  className="mt-2 flex items-start gap-2 text-xs text-neutral-600 cursor-pointer select-none"
                  data-testid="quote-manually-settled-wrapper"
                >
                  <Checkbox
                    checked={Boolean(client.quote.manually_settled)}
                    onCheckedChange={(checked) => patchQuote({ manually_settled: Boolean(checked) })}
                    className="mt-0.5"
                    data-testid="quote-manually-settled-checkbox"
                  />
                  <span className="leading-snug">
                    <span className="font-medium text-neutral-700">Saldato manualmente</span>
                    <span className="text-neutral-500 block text-[11px]">
                      Chiusura amministrativa del saldo. Non registra un nuovo acconto.
                    </span>
                  </span>
                </label>
              </div>
            </div>

            <div className="border-t border-border pt-5 flex items-baseline justify-between">
              <span className="label-uppercase text-primary">Totale</span>
              <span className="font-serif-heading text-4xl text-primary" data-testid="quote-total">{fmtEuro(client.quote.total)}</span>
            </div>

            <div data-testid="quote-quick-notes-section">
              <Label className="label-uppercase">Note rapide</Label>
              <p className="text-xs text-neutral-500 mt-1">
                Selezionale per aggiungerle automaticamente al preventivo. Cumulabili, senza duplicati.
                {(client.language || "it").toLowerCase() !== "it" && (
                  <span className="ml-1 text-amber-700">
                    Verranno mostrate al cliente in inglese (lingua cliente: {(client.language || "").toUpperCase()}).
                  </span>
                )}
              </p>
              <div className="mt-3 space-y-2.5">
                {quickNotesForLanguage(client.language).map((qn) => {
                  const selected = (client.quote.quick_notes || []).includes(qn.id);
                  const toggle = (checked) => {
                    const current = Array.isArray(client.quote.quick_notes) ? client.quote.quick_notes : [];
                    const next = checked
                      ? (current.includes(qn.id) ? current : [...current, qn.id])
                      : current.filter((x) => x !== qn.id);
                    patchQuote({ quick_notes: next });
                  };
                  return (
                    <label
                      key={qn.id}
                      htmlFor={`quick-note-${qn.id}`}
                      className="flex items-start gap-3 p-3 rounded-sm border border-border hover:bg-secondary/40 cursor-pointer transition-colors"
                      data-testid={`quote-quick-note-${qn.id}`}
                    >
                      <Checkbox
                        id={`quick-note-${qn.id}`}
                        checked={selected}
                        onCheckedChange={(v) => toggle(Boolean(v))}
                        className="mt-0.5"
                        data-testid={`quote-quick-note-checkbox-${qn.id}`}
                      />
                      <span className="text-sm text-neutral-700 leading-relaxed select-none">
                        {qn.label}
                      </span>
                    </label>
                  );
                })}
              </div>
            </div>

            <div>
              <Label className="label-uppercase">Note preventivo</Label>
              {(client.language || "it").toLowerCase() !== "it" && (
                <p
                  className="text-xs text-amber-700 mt-1"
                  data-testid="quote-notes-language-hint"
                >
                  Scrivi le note nella lingua del cliente — non verranno tradotte automaticamente.
                </p>
              )}
              <Textarea rows={3} value={client.quote.notes || ""} onChange={(e) => patchQuote({ notes: e.target.value })} className="mt-1 rounded-sm" />
            </div>

            {client.quote.sent_at && (
              <div className="text-xs text-neutral-500 flex items-center gap-2 flex-wrap" data-testid="quote-sent-info">
                <span>
                  Inviato
                  {client.quote.sent_method && (
                    <> via <strong className="font-medium text-neutral-700">
                      {client.quote.sent_method === "email" ? "Email"
                        : client.quote.sent_method === "whatsapp" ? "WhatsApp"
                        : "Link"}
                    </strong></>
                  )}
                  {" il "}{new Date(client.quote.sent_at).toLocaleString("it-IT")}
                </span>
                {client.quote.delivery_status && client.quote.delivery_status !== "sent" && (
                  <span
                    className={
                      ["bounced", "complained", "error"].includes(client.quote.delivery_status)
                        ? "inline-flex items-center gap-1 px-2 py-0.5 rounded-sm border border-rose-200 bg-rose-50 text-rose-700"
                        : client.quote.delivery_status === "opened" || client.quote.delivery_status === "clicked"
                        ? "inline-flex items-center gap-1 px-2 py-0.5 rounded-sm border border-amber-200 bg-amber-50 text-amber-700"
                        : "inline-flex items-center gap-1 px-2 py-0.5 rounded-sm border border-emerald-200 bg-emerald-50 text-emerald-700"
                    }
                    data-testid="quote-delivery-status-pill"
                  >
                    {{
                      delivered: "Consegnata",
                      opened: "Aperta",
                      clicked: "Link cliccato",
                      bounced: "Email non recapitata",
                      complained: "Segnalata come spam",
                      error: "Errore di invio",
                    }[client.quote.delivery_status] || client.quote.delivery_status}
                  </span>
                )}
              </div>
            )}
          </div>

          {/* MOBILE: sticky bottom action bar — Condividi (single CTA) + autosave badge.
              Rendered only when this tab is active (shadcn Tabs keeps all TabsContent
              mounted via CSS, so we must gate on activeTab to avoid the bar leaking
              across tabs). */}
          {activeTab === "quote" && (
            <QuoteMobileActionBar
              onShare={openShareDialog}
              busy={preparingPdf}
              disabled={!sendQuote && !sendContract}
              autosaveStatus={autosaveStatus}
              dirty={client && JSON.stringify(client) !== lastSavedSnapshotRef.current}
            />
          )}
        </TabsContent>

        <TabsContent value="contract" className="mt-6">
          <div className="bg-white border border-border rounded-sm p-8 space-y-6">
            <div className="flex items-start justify-between flex-wrap gap-3">
              <div>
                <h2 className="font-serif-heading text-2xl">Contratto</h2>
                <p className="text-sm text-neutral-500 mt-1">Generato automaticamente dai dati del preventivo. Personalizza condizioni e termini.</p>
              </div>
              <div className="flex items-center gap-3 flex-wrap">
                <Badge className={`rounded-sm border ${contractStatusColor}`} data-testid="contract-status-badge">
                  {CONTRACT_STATUSES.find((s) => s.v === client.contract.status)?.label || client.contract.status}
                </Badge>
                <Select value={client.contract.status} onValueChange={(v) => patchContract("status", v)}>
                  <SelectTrigger className="w-44 rounded-sm" data-testid="contract-status-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {CONTRACT_STATUSES.map((s) => <SelectItem key={s.v} value={s.v}>{s.label}</SelectItem>)}
                  </SelectContent>
                </Select>
                {/* Tipo contratto: Standard | Personalizzato */}
                <div className="flex items-center gap-2" data-testid="contract-mode-wrapper">
                  <Badge
                    variant="outline"
                    className="rounded-sm border-primary/30 text-primary"
                    data-testid="contract-mode-badge"
                  >
                    {(client.contract.mode || "standard") === "custom" ? "Personalizzato" : "Standard"}
                  </Badge>
                  <Select
                    value={client.contract.mode || "standard"}
                    onValueChange={setContractMode}
                  >
                    <SelectTrigger className="w-48 rounded-sm" data-testid="contract-mode-select">
                      <ScrollText className="w-4 h-4 mr-2 text-primary" strokeWidth={1.5} />
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="standard">Standard</SelectItem>
                      <SelectItem value="custom">Personalizzato</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <Button variant="outline" className="rounded-sm" onClick={() => openPdf("contract")} data-testid="contract-pdf-button">
                  <Download className="w-4 h-4 mr-2" strokeWidth={1.5} /> Visualizza PDF
                </Button>
              </div>
            </div>

            {(client.contract.mode || "standard") === "custom" && (
              <div className="text-xs text-neutral-500 -mt-2">
                <Link
                  to="/app/settings#contract-template"
                  className="text-primary hover:underline inline-flex items-center gap-1"
                  data-testid="contract-edit-custom-link"
                >
                  <ExternalLink className="w-3 h-3" strokeWidth={1.5} /> Modifica contratto personalizzato
                </Link>
              </div>
            )}

            <div className="grid md:grid-cols-3 gap-5 bg-secondary/50 p-5 rounded-sm border border-border">
              <div>
                <div className="label-uppercase">Totale pattuito</div>
                <div className="font-serif-heading text-2xl mt-1">{fmtEuro(client.quote?.total)}</div>
              </div>
              <div>
                <div className="label-uppercase">Acconto</div>
                <div className="font-serif-heading text-2xl mt-1">{fmtEuro(client.quote?.deposit)}</div>
              </div>
              <div>
                <div className="label-uppercase">Saldo</div>
                {client.quote?.manually_settled ? (
                  <div className="mt-1" data-testid="contract-balance-settled">
                    <span
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-sm bg-emerald-50 border border-emerald-300 text-emerald-800 text-sm font-bold uppercase tracking-wider"
                    >
                      <CheckCircle2 className="w-4 h-4" strokeWidth={2} />
                      Saldato
                    </span>
                  </div>
                ) : (
                  <div className="font-serif-heading text-2xl mt-1">{fmtEuro(client.quote?.balance)}</div>
                )}
                <label
                  className="mt-2 flex items-start gap-2 text-xs text-neutral-600 cursor-pointer select-none"
                  data-testid="contract-manually-settled-wrapper"
                >
                  <Checkbox
                    checked={Boolean(client.quote?.manually_settled)}
                    onCheckedChange={(checked) => patchQuote({ manually_settled: Boolean(checked) })}
                    className="mt-0.5"
                    data-testid="contract-manually-settled-checkbox"
                  />
                  <span className="leading-snug">
                    <span className="font-medium text-neutral-700">Saldato manualmente</span>
                  </span>
                </label>
              </div>
            </div>

            <div>
              <Label className="label-uppercase">Condizioni generali</Label>
              <Textarea rows={18} value={client.contract.conditions || ""} onChange={(e) => patchContract("conditions", e.target.value)} className="mt-1 rounded-sm font-mono text-xs leading-relaxed" data-testid="contract-conditions" />
              <p className="text-[11px] text-neutral-400 mt-1.5">Le interruzioni di riga vengono preservate nel PDF.</p>
            </div>
            <div>
              <Label className="label-uppercase">Termini di pagamento</Label>
              <Textarea rows={6} value={client.contract.payment_terms || ""} onChange={(e) => patchContract("payment_terms", e.target.value)} className="mt-1 rounded-sm font-mono text-xs leading-relaxed" data-testid="contract-payment-terms" />
            </div>

            <div className="grid md:grid-cols-3 gap-4 text-xs text-neutral-500 border-t border-border pt-5">
              {client.contract.generated_at && <div>Generato il {new Date(client.contract.generated_at).toLocaleString("it-IT")}</div>}
              {client.contract.sent_at && <div>Inviato il {new Date(client.contract.sent_at).toLocaleString("it-IT")}</div>}
              {client.contract.signed_at && <div>Firmato il {new Date(client.contract.signed_at).toLocaleString("it-IT")}</div>}
            </div>
          </div>
        </TabsContent>

        <TabsContent value="timeline" className="mt-6">
          <PlanLock module="timeline" requiredPlan="pro" label="Timeline operativa">
            <TimelineTab
              client={client}
              onClientChange={(c) => setClient(c)}
              onSavedTimeline={(tl) => setClient((c) => ({ ...c, timeline: tl }))}
            />
          </PlanLock>
        </TabsContent>

        <TabsContent value="wedding-day" className="mt-6">
          {/* No PlanLock here on purpose: the Wedding Day tab is a small,
              high-value assistant that should be accessible across plans
              (it depends only on data the operator already filled in).
              Wrapped in SafeBoundary so any runtime error inside the tab
              (Safari iPad date/locale quirks, transient API shape changes…)
              degrades gracefully into a contained fallback instead of
              taking down the whole client detail page. */}
          <SafeBoundary label='la sezione "Wedding Day"'>
            <WeddingDayTab clientId={client.id} />
          </SafeBoundary>
        </TabsContent>

        <TabsContent value="map" className="mt-6">
          <MapTab client={client} patch={patch} mapsUrl={mapsUrl} />
        </TabsContent>
      </Tabs>

      {/* Share dialog: send quote and/or contract based on operator's checkbox selection */}
      <Dialog open={shareOpen} onOpenChange={setShareOpen}>
        <DialogContent className="rounded-sm sm:max-w-lg" data-testid="share-quote-contract-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif-heading text-2xl">
              {sendContract && sendQuote
                ? "Condividi preventivo e contratto"
                : sendContract
                  ? "Condividi contratto"
                  : "Condividi preventivo"}
            </DialogTitle>
            <DialogDescription>
              {preparingPdf
                ? "Generazione del PDF in corso…"
                : sendContract
                  ? "Scegli come inviare il documento. Lo stato verrà aggiornato a Inviato dopo la condivisione."
                  : "Solo il preventivo verrà condiviso. Il contratto resterà privato e non sarà visibile al cliente."}
            </DialogDescription>
          </DialogHeader>

          {/* PDF status */}
          <div className={`flex items-center gap-3 p-3 rounded-sm border ${
            preparingPdf
              ? "bg-secondary/40 border-border"
              : mergedPdfBlob
                ? "bg-emerald-50 border-emerald-200"
                : "bg-amber-50 border-amber-200"
          }`}>
            {preparingPdf ? (
              <Loader2 className="w-4 h-4 text-primary animate-spin" strokeWidth={1.5} />
            ) : mergedPdfBlob ? (
              <FileText className="w-4 h-4 text-emerald-700" strokeWidth={1.5} />
            ) : (
              <FileText className="w-4 h-4 text-amber-700" strokeWidth={1.5} />
            )}
            <div className="text-xs">
              <div className="font-medium">{buildPdfFilename()}</div>
              <div className="text-neutral-500">
                {preparingPdf
                  ? "Unione del preventivo e del contratto…"
                  : mergedPdfBlob
                    ? `${(mergedPdfBlob.size / 1024).toFixed(0)} KB · pronto`
                    : "PDF non disponibile"}
              </div>
            </div>
          </div>

          {/* Actions grid */}
          <div className="grid sm:grid-cols-2 gap-2 mt-2">
            <ShareAction
              icon={LinkIcon}
              label="Copia link cliente"
              hint={clientShareToken ? `${window.location.origin}/cliente/${clientShareToken.slice(0, 12)}…` : "Pagina pubblica con accetto/rifiuto"}
              busy={shareBusyAction === "link"}
              disabled={preparingPdf || shareBusyAction !== ""}
              onClick={copyClientLink}
              testid="share-action-copy-link"
            />
            <ShareAction
              icon={Mail}
              label="Invia via Email"
              hint={client?.email ? `Invio automatico a ${client.email}` : "Email cliente mancante"}
              busy={shareBusyAction === "email"}
              disabled={preparingPdf || shareBusyAction !== "" || !client?.email}
              onClick={shareViaEmail}
              testid="share-action-email"
            />
            <ShareAction
              icon={MessageCircle}
              label="Invia via WhatsApp"
              hint={client?.phone ? `wa.me/${client.phone.replace(/[^\d]/g, "")}` : "Apre WhatsApp web"}
              busy={shareBusyAction === "whatsapp"}
              disabled={preparingPdf || shareBusyAction !== ""}
              onClick={shareViaWhatsApp}
              testid="share-action-whatsapp"
            />
            <ShareAction
              icon={Download}
              label="Scarica PDF"
              hint="Salva il documento sul dispositivo"
              busy={shareBusyAction === "download"}
              disabled={preparingPdf || shareBusyAction !== ""}
              onClick={downloadMergedPdf}
              testid="share-action-download"
            />
            <ShareAction
              icon={Smartphone}
              label="Condividi…"
              hint={canWebShare ? "Menu nativo (AirDrop, Mail, WhatsApp…)" : "Non disponibile su questo dispositivo"}
              busy={shareBusyAction === "system"}
              disabled={preparingPdf || !canWebShare || shareBusyAction !== ""}
              onClick={shareViaSystem}
              testid="share-action-system"
            />
          </div>

          {clientShareToken && (
            <a
              href={`${window.location.origin}/cliente/${clientShareToken}`}
              target="_blank"
              rel="noreferrer"
              className="text-xs text-primary hover:underline mt-2 inline-flex items-center gap-1"
              data-testid="share-preview-public"
            >
              <ExternalLink className="w-3 h-3" strokeWidth={1.5} /> Anteprima pagina cliente
            </a>
          )}

          <p className="text-[11px] text-neutral-400 mt-2">
            Email e WhatsApp non permettono allegati automatici via link: il PDF viene scaricato in automatico
            così potrai trascinarlo nel messaggio. Il link cliente apre invece una pagina interattiva con
            i pulsanti Accetto / Rifiuto.
          </p>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Single action card inside the share dialog
// Predefined vendor "slots" for the Partner / Fornitori tab. The order
// matches the user-facing list and is rendered first; any free-form
// "other" vendors come below.
//
// NOTE (Feb 2026): the wedding_planner slot has been promoted to a dedicated,
// fully-featured block (see <WeddingPlannerBlock />) rendered above the
// predefined slots list. Legacy data living under vendors[type=wedding_planner]
// is auto-migrated server-side into client.wedding_planner on first GET, so
// the data isn't lost — we simply remove the row from the generic list to
// avoid a duplicate UI.
const VENDOR_SLOTS = [
  { slug: "photo_video",       label: "Fotografo / Videomaker" },
  { slug: "florist",           label: "Fiorista" },
  { slug: "music_dj",          label: "Musica / DJ" },
  { slug: "atelier",           label: "Atelier / Abito da sposa" },
  { slug: "makeup_external",   label: "Makeup artist (se esterno)" },
  { slug: "hair_external",     label: "Hair stylist (se esterno)" },
  { slug: "location",          label: "Location" },
];

function VendorsSection({ client, patch, team }) {
  const vendors = Array.isArray(client.vendors) ? client.vendors : [];

  // Look up the row for a predefined slot by its slug.
  const getSlot = (slug) => vendors.find((v) => v.type === slug) || null;

  // Update one field of a predefined slot. If no row exists yet for that slug
  // we create one inline so the operator just types and saves — no "add" step.
  const updateSlot = (slug, key, value) => {
    const list = [...vendors];
    const idx = list.findIndex((v) => v.type === slug);
    if (idx >= 0) {
      list[idx] = { ...list[idx], [key]: value };
    } else {
      list.push({ type: slug, name: "", instagram: "", notes: "", [key]: value });
    }
    patch("vendors", list);
  };

  // Free-form ("other") vendor rows. We address them by their position
  // among the "other" rows (`otherIdx`), not by absolute array index, to
  // keep the JSX trivial.
  const others = vendors
    .map((v, absIdx) => ({ v, absIdx }))
    .filter(({ v }) => v.type === "other");

  const updateOther = (otherIdx, key, value) => {
    const target = others[otherIdx];
    if (!target) return;
    const list = [...vendors];
    list[target.absIdx] = { ...list[target.absIdx], [key]: value };
    patch("vendors", list);
  };

  const addOther = () => {
    patch("vendors", [
      ...vendors,
      { type: "other", custom_label: "", name: "", instagram: "", notes: "" },
    ]);
  };

  const removeOther = (otherIdx) => {
    const target = others[otherIdx];
    if (!target) return;
    const list = vendors.filter((_, i) => i !== target.absIdx);
    patch("vendors", list);
  };

  return (
    <>
      {/* ── Mobile (< md): redesigned card-based view (Feb 2026 sprint).
          Same callbacks, same fields, just touch-friendly. ── */}
      <div className="md:hidden">
        <VendorsMobileView
          client={client}
          team={team}
          VENDOR_SLOTS={VENDOR_SLOTS}
          getSlot={getSlot}
          updateSlot={updateSlot}
          others={others}
          updateOther={updateOther}
          addOther={addOther}
          removeOther={removeOther}
        />
      </div>

      {/* ── Desktop (≥ md): original linear layout, completely untouched. ── */}
      <div className="hidden md:block bg-white border border-border rounded-sm p-8 space-y-8" data-testid="vendors-section">
      {/* Intro */}
      <header className="space-y-2">
        <div className="flex items-center gap-3">
          <h2 className="font-serif-heading text-2xl">Partner / Fornitori</h2>
          <span
            className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.2em] px-2.5 py-1 rounded-full"
            style={{ backgroundColor: "rgba(200,169,126,0.12)", color: "#7B6A45", border: "1px solid rgba(200,169,126,0.4)" }}
            data-testid="vendors-internal-badge"
          >
            <Lock className="w-3 h-3" strokeWidth={1.5} /> Solo uso interno
          </span>
        </div>
        <p className="text-sm text-neutral-600 leading-relaxed max-w-3xl">
          Sezione interna per annotare i fornitori coinvolti nel matrimonio.
          Può inserire anche i riferimenti Instagram per facilitare tag e
          collaborazioni durante la pubblicazione dei contenuti.
        </p>
        <p className="text-xs text-neutral-500 italic">
          Queste informazioni non sono mai visibili nel link cliente, nel preventivo o nel PDF.
        </p>
      </header>

      {/* Wedding Planner — extended dedicated block (Feb 2026) */}
      <WeddingPlannerBlock client={client} patch={patch} />

      {/* Predefined slots */}
      <div className="space-y-5 pt-2" data-testid="vendors-predefined-list">
        <h3 className="font-serif-heading text-xl">Altri partner principali</h3>
        {VENDOR_SLOTS.map((slot) => {
          const row = getSlot(slot.slug) || { name: "", instagram: "", notes: "" };
          return (
            <VendorRow
              key={slot.slug}
              label={slot.label}
              testid={`vendor-row-${slot.slug}`}
              name={row.name || ""}
              instagram={row.instagram || ""}
              notes={row.notes || ""}
              onChangeName={(v) => updateSlot(slot.slug, "name", v)}
              onChangeInstagram={(v) => updateSlot(slot.slug, "instagram", v)}
              onChangeNotes={(v) => updateSlot(slot.slug, "notes", v)}
            />
          );
        })}
      </div>

      {/* Free-form "Altri fornitori" */}
      <div className="border-t border-border pt-6 space-y-5" data-testid="vendors-other-list">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h3 className="font-serif-heading text-xl">Altri fornitori</h3>
            <p className="text-xs text-neutral-500 mt-1">
              Aggiunga qui qualsiasi altro fornitore non in elenco (es. cake designer, calligrafo, intrattenimento bambini).
            </p>
          </div>
          <Button
            variant="outline"
            size="sm"
            className="rounded-sm"
            onClick={addOther}
            data-testid="vendors-add-other"
          >
            <Plus className="w-4 h-4 mr-2" strokeWidth={1.5} /> Aggiungi fornitore
          </Button>
        </div>

        {others.length === 0 ? (
          <p className="text-sm text-neutral-400 italic">Nessun altro fornitore aggiunto.</p>
        ) : (
          others.map(({ v }, i) => (
            <VendorRow
              key={`other-${v.absIdx ?? i}`}
              testid={`vendor-row-other-${i}`}
              customLabelEditable
              label={v.custom_label || "Tipologia fornitore"}
              labelValue={v.custom_label || ""}
              onChangeLabel={(val) => updateOther(i, "custom_label", val)}
              name={v.name || ""}
              instagram={v.instagram || ""}
              notes={v.notes || ""}
              onChangeName={(val) => updateOther(i, "name", val)}
              onChangeInstagram={(val) => updateOther(i, "instagram", val)}
              onChangeNotes={(val) => updateOther(i, "notes", val)}
              onRemove={() => removeOther(i)}
            />
          ))
        )}
      </div>
      </div>
    </>
  );
}

function VendorRow({
  label,
  testid,
  name,
  instagram,
  notes,
  onChangeName,
  onChangeInstagram,
  onChangeNotes,
  onRemove,
  customLabelEditable = false,
  labelValue = "",
  onChangeLabel,
}) {
  // Light auto-prefix of the Instagram handle. Operators tend to paste either
  // "@handle", "handle", or a full instagram URL — store always as "@handle".
  const handleInstagramBlur = (e) => {
    const v = (e.target.value || "").trim();
    if (!v) return;
    let h = v;
    if (h.startsWith("https://")) {
      try {
        const u = new URL(h);
        h = u.pathname.replace(/^\/+|\/+$/g, "").split("/")[0] || "";
      } catch { /* ignore */ }
    }
    h = h.replace(/^@+/, "");
    if (h && h !== v.replace(/^@+/, "")) {
      onChangeInstagram("@" + h);
    } else if (h) {
      onChangeInstagram("@" + h);
    }
  };

  return (
    <div
      className="rounded-sm border border-border p-5 space-y-4"
      style={{ backgroundColor: "#FFFDFA" }}
      data-testid={testid}
    >
      <div className="flex items-start justify-between gap-3 flex-wrap">
        {customLabelEditable ? (
          <Input
            placeholder="Tipologia fornitore (es. Cake Designer)"
            value={labelValue}
            onChange={(e) => onChangeLabel?.(e.target.value)}
            className="rounded-sm font-serif-heading text-lg flex-1 min-w-[220px]"
            data-testid={`${testid}-custom-label`}
          />
        ) : (
          <h4 className="font-serif-heading text-lg leading-tight">{label}</h4>
        )}
        {onRemove && (
          <Button
            variant="ghost"
            size="sm"
            className="rounded-sm text-neutral-400 hover:text-red-600"
            onClick={onRemove}
            data-testid={`${testid}-remove`}
          >
            <Trash2 className="w-4 h-4" strokeWidth={1.5} />
          </Button>
        )}
      </div>
      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <Label className="label-uppercase">Nome fornitore</Label>
          <Input
            value={name}
            onChange={(e) => onChangeName(e.target.value)}
            className="mt-1 rounded-sm"
            placeholder="Es. Studio Bianchi"
            data-testid={`${testid}-name`}
          />
        </div>
        <div>
          <Label className="label-uppercase flex items-center gap-1.5">
            <Instagram className="w-3 h-3" strokeWidth={1.5} /> Instagram
          </Label>
          <Input
            value={instagram}
            onChange={(e) => onChangeInstagram(e.target.value)}
            onBlur={handleInstagramBlur}
            className="mt-1 rounded-sm"
            placeholder="@nomeprofilo"
            data-testid={`${testid}-instagram`}
          />
        </div>
      </div>
      <div>
        <Label className="label-uppercase">Note (facoltativo)</Label>
        <Input
          value={notes}
          onChange={(e) => onChangeNotes(e.target.value)}
          className="mt-1 rounded-sm"
          placeholder="Es. referente, orari preferiti, accordi presi…"
          data-testid={`${testid}-notes`}
        />
      </div>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", className = "", testid, numberOpts }) {
  // When the field is numeric, defer to NumberField — empty-by-default
  // display, decimal keypad on iPad, no native spinners. The parent keeps
  // receiving a Number through `onChange` (consistent with the rest of
  // the form callbacks).
  const isNumber = type === "number";
  return (
    <div className={className}>
      <Label className="label-uppercase">{label}</Label>
      {isNumber ? (
        <NumberField
          value={value}
          onChange={onChange}
          className="mt-1 rounded-sm"
          data-testid={testid}
          {...(numberOpts || {})}
        />
      ) : (
        <Input
          type={type}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="mt-1 rounded-sm"
          data-testid={testid}
        />
      )}
    </div>
  );
}

// Compact, Google-Docs style autosave indicator. Shown below the toolbar.
// status: 'idle' | 'saving' | 'saved' | 'error'
// dirty: true if there are local edits not yet persisted
function AutosaveBadge({ status, dirty }) {
  let label = "";
  let icon = null;
  let tone = "text-neutral-500";
  if (status === "saving") {
    label = "Salvataggio in corso…";
    icon = <Loader2 className="w-3.5 h-3.5 animate-spin" strokeWidth={1.5} />;
    tone = "text-amber-600";
  } else if (status === "saved") {
    label = "Salvato automaticamente";
    icon = <CheckCircle2 className="w-3.5 h-3.5" strokeWidth={1.5} />;
    tone = "text-emerald-600";
  } else if (status === "error") {
    label = "Errore salvataggio · usa il bottone Salva";
    icon = <AlertCircle className="w-3.5 h-3.5" strokeWidth={1.5} />;
    tone = "text-red-600";
  } else if (dirty) {
    label = "Modifiche non salvate";
    icon = <AlertCircle className="w-3.5 h-3.5" strokeWidth={1.5} />;
    tone = "text-neutral-500";
  } else {
    return null; // Idle and clean → render nothing to keep the UI calm
  }
  return (
    <div className={`flex items-center gap-1.5 text-xs ${tone}`} data-testid="autosave-status" data-status={status}>
      {icon}
      <span>{label}</span>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* DEPOSITS EDITOR — multi-row "Acconti ricevuti" list.                */
/* ------------------------------------------------------------------ */
/**
 * Replaces the old single "Acconto (€)" input. Lets the operator log
 * unlimited deposit installments (date + amount + notes) and emits the
 * updated array up to the parent. The parent is responsible for:
 *  - syncing `quote.deposit = sum(deposits.amount)` so legacy consumers
 *    (status bar, PDF templates, public payload fallback) keep working
 *  - re-running `recalcQuote` to update `quote.balance`
 *
 * Empty-state behaviour: when the list is empty AND the legacy single
 * `deposit` value is 0, we surface a single pristine row pre-filled with
 * today's date so the user can start typing the amount immediately —
 * matching the brief's "prima riga vuota precompilata" requirement.
 *
 * Why a separate component: keeps the giant ClientDetail render tree
 * readable AND avoids re-rendering the entire quote on every keystroke
 * inside a deposit row (React only re-runs this subtree).
 */
function DepositsEditor({ deposits, onChange }) {
  const todayIso = () => new Date().toISOString().slice(0, 10);
  // SAFE INIT: if the parent passes nothing/empty, we surface a single
  // pristine row with today's date so the operator can start typing
  // immediately. Crucially we DO NOT recompute `todayIso()` on every
  // render — that would reset the date the moment the parent re-renders
  // for an unrelated reason (e.g. autosave round-trip). The seeded date
  // is fixed once via `seededDateRef` and reused for the lifetime of the
  // editor when the deposits array is still empty.
  //
  // `amount` defaults to the NUMBER 0 (not an empty string) so the
  // Pydantic `DepositEntry.amount: float` validator never sees an
  // unparseable string — that previously surfaced as a toast on every
  // "+ Aggiungi acconto" click. NumberField renders 0 as a blank input
  // visually, so UX is unchanged.
  const seededDateRef = useRef(todayIso());
  const rows = (Array.isArray(deposits) && deposits.length > 0)
    ? deposits
    : [{ date: seededDateRef.current, amount: 0, notes: "" }];

  /** Coerce a user-supplied amount to a finite number for transport.
   *  Defense-in-depth — even if a row somehow holds "" or NaN before we
   *  hit save, this guarantees the backend always receives a valid float. */
  const toNumberAmount = (v) => {
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
  };

  const update = (idx, patch) => {
    // Always start from the freshest props snapshot — preserves every
    // other field on the row, especially the user-picked date when the
    // user types in the amount field. We also normalize the amount on
    // every keystroke so the in-memory state is always backend-safe.
    const next = rows.map((r, i) =>
      i === idx
        ? { ...r, ...patch, amount: toNumberAmount(("amount" in patch) ? patch.amount : r.amount) }
        : { ...r, amount: toNumberAmount(r.amount) },
    );
    onChange(next);
  };
  const remove = (idx) => {
    const next = rows.filter((_, i) => i !== idx).map((r) => ({ ...r, amount: toNumberAmount(r.amount) }));
    // Never collapse to a fully empty array when the user explicitly
    // removes the last row — the empty-state synthesis will surface a
    // fresh seeded row on the next render, which is the contracted
    // behaviour ("empty list → one pristine starter row").
    onChange(next);
  };
  const add = () => {
    // Use today as the default for newly-added rows. We DON'T reuse
    // `seededDateRef` here so each "+ Aggiungi" gets the date as of the
    // click moment, not the page-mount moment. Amount=0 (not "") so
    // Pydantic validation never fires the "invalid number" toast.
    onChange([
      ...rows.map((r) => ({ ...r, amount: toNumberAmount(r.amount) })),
      { date: todayIso(), amount: 0, notes: "" },
    ]);
  };

  return (
    <div className="mt-1 space-y-2" data-testid="quote-deposits-editor">
      {rows.map((row, idx) => (
        <div
          key={idx}
          className="grid grid-cols-12 gap-2 items-start bg-neutral-50/40 border border-border rounded-sm px-2 py-2"
          data-testid={`quote-deposit-row-${idx}`}
        >
          <div className="col-span-12 sm:col-span-3">
            <Input
              type="date"
              value={row.date || ""}
              onChange={(e) => update(idx, { date: e.target.value })}
              className="h-9 rounded-sm text-xs min-w-[140px]"
              data-testid={`quote-deposit-date-${idx}`}
            />
          </div>
          <div className="col-span-6 sm:col-span-3">
            <NumberField
              value={row.amount}
              onChange={(n) => update(idx, { amount: n })}
              min={0}
              placeholder="0,00"
              className="h-9 rounded-sm"
              data-testid={`quote-deposit-amount-${idx}`}
            />
          </div>
          <div className="col-span-5 sm:col-span-5">
            <Input
              type="text"
              value={row.notes || ""}
              onChange={(e) => update(idx, { notes: e.target.value })}
              placeholder="Note (es. prova, firma contratto…)"
              className="h-9 rounded-sm text-xs"
              data-testid={`quote-deposit-notes-${idx}`}
            />
          </div>
          <div className="col-span-1 flex items-center justify-end">
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={() => remove(idx)}
              disabled={rows.length === 1 && !(parseFloat(row.amount) || 0) && !row.notes}
              className="h-9 w-9 text-neutral-400 hover:text-red-600 hover:bg-red-50"
              title="Elimina acconto"
              data-testid={`quote-deposit-remove-${idx}`}
            >
              <Trash2 className="w-4 h-4" strokeWidth={1.5} />
            </Button>
          </div>
        </div>
      ))}
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={add}
        className="text-xs text-neutral-600 hover:text-primary"
        data-testid="quote-deposit-add"
      >
        <Plus className="w-3.5 h-3.5 mr-1" strokeWidth={1.6} />
        Aggiungi acconto
      </Button>
    </div>
  );
}

